import type { Money } from "@/lib/domain/Money";
import { useHTTP, useHTTPMutation, type HTTPResponse } from "@/lib/http";
import { useQueryClient, type MutationFunction } from "@tanstack/react-query";
import type { PriceOverrideDetailsResponseModel } from "@/features/priceOverrides/api/usePriceOverrideDetails";
import { clearCacheAfterPriceOverrideMutation } from "@/features/priceOverrides/api/clearCacheAfterPriceOverrideMutation";
import { paginatedUpdate } from "@/lib/http/paginatedUpdate";

export interface PriceOverrideUpdateRequestModel {
	readonly price: Money;
}

export interface PriceOverrideUpdateRequestProperties {
	readonly requestModel: PriceOverrideUpdateRequestModel;
	readonly priceOverrideId: string;
}

export const usePriceOverrideUpdate = () => {
	const http = useHTTP("Required");
	const queryClient = useQueryClient();

	const mutationFn: MutationFunction<HTTPResponse<PriceOverrideDetailsResponseModel>, PriceOverrideUpdateRequestProperties> = requestProperties => {
		return http<PriceOverrideDetailsResponseModel, PriceOverrideUpdateRequestModel>({
			method: "PUT",
			url: `/price-override/${requestProperties.priceOverrideId}`,
			body: requestProperties.requestModel
		});
	};

	return useHTTPMutation({
		mutationFn,
		onSuccess: (data) => {
			queryClient.setQueryData(["priceOverrideDetails"], paginatedUpdate(data.body, x => x.priceOverrideId === data.body.priceOverrideId));
			clearCacheAfterPriceOverrideMutation(queryClient);
		}
	});
};
